import React, { useEffect, useState } from "react";
import { Users, X, Loader2, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";

export const ShareDialog = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/collaborators`);
      setData(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      await api.post(`/projects/${projectId}/share`, { email });
      toast.success("Teammate invited");
      setEmail("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setInviting(false); }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/projects/${projectId}/share/${uid}`);
      await load();
    } catch { toast.error("Could not remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white" data-testid="share-btn">
          <Users className="w-4 h-4 mr-1" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Share project</DialogTitle>
          <DialogDescription className="text-white/50">Invite a teammate by email to view and edit this project.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" /></div>
        ) : (
          <div className="space-y-4">
            {data.is_owner && (
              <form onSubmit={invite} className="flex gap-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="teammate@email.com"
                  className="bg-black border-white/15 focus-visible:ring-[#00E5FF]" data-testid="share-email-input" />
                <Button type="submit" disabled={inviting} className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 font-medium shrink-0" data-testid="share-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invite"}
                </Button>
              </form>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-black border border-white/10 rounded-lg p-3" data-testid="collab-owner">
                <div>
                  <p className="text-sm">{data.owner.name}</p>
                  <p className="text-xs text-white/40">{data.owner.email}</p>
                </div>
                <span className="text-xs text-[#FFD500] flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> Owner</span>
              </div>
              {data.collaborators.map((c) => (
                <div key={c.id} className="flex items-center justify-between bg-black border border-white/10 rounded-lg p-3" data-testid={`collab-${c.id}`}>
                  <div>
                    <p className="text-sm">{c.name}</p>
                    <p className="text-xs text-white/40">{c.email}</p>
                  </div>
                  {data.is_owner && (
                    <button onClick={() => remove(c.id)} className="text-white/40 hover:text-[#FF007F]" data-testid={`remove-collab-${c.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {data.collaborators.length === 0 && (
                <p className="text-xs text-white/40 py-2">No teammates yet.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
